/**
 * Smplify MCP OAuth client (RFC 7591 DCR + RFC 7636 PKCE).
 *
 * Single-token-across-backends strategy: the JWT returned by
 * https://mcp.smplify.com/token is accepted by api.smplify.com,
 * edr-demo.smplify.com, and lighthouse-demo.smplify.com. We persist it
 * via the existing `apiClient` token storage so every fetch wrapper
 * (apiClient, edrApi, lighthouseApi) automatically picks it up.
 *
 * Flow:
 *   1. ensureClientId()     — one-time POST /register (cached forever)
 *   2. startAuthorization() — redirect to /authorize with PKCE+state
 *   3. completeAuthorization(code, state) — POST /token, store tokens
 *   4. refreshAccessToken() — opportunistic refresh near expiry
 */

import { supabase } from "@/integrations/supabase/client";
import { setMcpAccessToken, setTenantId, clearMcpAccessToken } from "./apiClient";

const ISSUER = "https://mcp.smplify.com";
const AUTHORIZE_URL = `${ISSUER}/authorize`;
const SCOPE = "mcp:read mcp:tools portal:read portal:write";
const RESOURCE = "https://api.smplify.com";

// localStorage keys (scoped + versioned so a future flow change is clean)
const LS_CLIENT_ID = "smplify_mcp_client_id_v2";
const LS_PKCE_VERIFIER = "smplify_mcp_pkce_verifier";
const LS_STATE = "smplify_mcp_oauth_state";
const LS_REFRESH = "smplify_mcp_refresh_token";
const LS_RETURN_TO = "smplify_mcp_return_to";

function getRedirectUri(): string {
  return `${window.location.origin}/oauth/callback`;
}

// --- PKCE helpers ---

function base64UrlEncode(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength = 48): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256(input: string): Promise<Uint8Array> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return new Uint8Array(hash);
}

// --- Dynamic Client Registration ---

type OAuthProxyOperation = "register" | "token" | "revoke";

interface OAuthProxyResponse<T = unknown> {
  ok: boolean;
  status: number;
  statusText?: string;
  body?: T;
  bodyText?: string;
}

async function mcpProxy<T>(operation: OAuthProxyOperation, payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<OAuthProxyResponse<T>>("mcp-oauth", {
    body: { operation, payload },
  });

  if (error) {
    throw new Error(error.message || "Unable to reach MCP OAuth bridge");
  }
  if (!data) {
    throw new Error("MCP OAuth bridge returned an empty response");
  }
  if (!data.ok) {
    throw new Error(
      `MCP OAuth ${operation} failed (${data.status}): ${data.bodyText || data.statusText || "Request failed"}`,
    );
  }
  return data.body as T;
}

export async function ensureClientId(): Promise<string> {
  const cached = localStorage.getItem(LS_CLIENT_ID);
  if (cached) return cached;

  const body = await mcpProxy<{ client_id?: string }>("register", {
    client_name: `Smplify Partner Portal (${window.location.host})`,
    redirect_uris: [getRedirectUri()],
    token_endpoint_auth_method: "none",
    grant_types: ["authorization_code", "refresh_token"],
    response_types: ["code"],
    scope: SCOPE,
  });

  if (!body.client_id) throw new Error("MCP DCR response missing client_id");
  localStorage.setItem(LS_CLIENT_ID, body.client_id);
  return body.client_id;
}

// --- Authorization redirect ---

export async function startAuthorization(returnTo?: string): Promise<void> {
  const clientId = await ensureClientId();
  const verifier = randomString(48);
  const challenge = base64UrlEncode(await sha256(verifier));
  const state = randomString(24);

  localStorage.setItem(LS_PKCE_VERIFIER, verifier);
  localStorage.setItem(LS_STATE, state);
  if (returnTo) localStorage.setItem(LS_RETURN_TO, returnTo);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: getRedirectUri(),
    scope: SCOPE,
    resource: RESOURCE,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.assign(`${AUTHORIZE_URL}?${params.toString()}`);
}

// --- Token decoding ---

interface McpJwtClaims {
  iss?: string;
  aud?: string;
  sub?: string;
  tenant_id?: string;
  tenantId?: string;
  kind?: string;
  exp?: number;
  iat?: number;
}

export function decodeJwt(token: string): McpJwtClaims | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

interface TokenResponse {
  access_token: string;
  token_type?: string;
  expires_in?: number;
  refresh_token?: string;
  scope?: string;
}

function persistTokens(tok: TokenResponse): void {
  if (!tok || typeof tok.access_token !== "string" || tok.access_token.length === 0) {
    throw new Error("MCP token response did not include an access_token. Sign-in cannot complete.");
  }
  setMcpAccessToken(tok.access_token);
  if (tok.refresh_token) {
    localStorage.setItem(LS_REFRESH, tok.refresh_token);
  }
  const claims = decodeJwt(tok.access_token);
  const tenant = claims?.tenant_id || claims?.tenantId || (claims as Record<string, unknown> | null)?.tenantid;
  if (tenant) setTenantId(String(tenant));
}

// --- Code exchange ---

export async function completeAuthorization(
  code: string,
  state: string,
): Promise<{ returnTo: string }> {
  const expectedState = localStorage.getItem(LS_STATE);
  const verifier = localStorage.getItem(LS_PKCE_VERIFIER);
  const returnTo = localStorage.getItem(LS_RETURN_TO) || "/getting-started";

  if (!expectedState || !verifier) {
    throw new Error("OAuth state missing — please restart sign-in.");
  }
  if (state !== expectedState) {
    throw new Error("OAuth state mismatch — possible tampering. Please retry.");
  }

  const clientId = await ensureClientId();
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: getRedirectUri(),
    client_id: clientId,
    code_verifier: verifier,
    resource: RESOURCE,
  });

  const tok = await mcpProxy<TokenResponse>("token", Object.fromEntries(body));
  persistTokens(tok);

  // Clean transient state regardless of success path
  localStorage.removeItem(LS_PKCE_VERIFIER);
  localStorage.removeItem(LS_STATE);
  localStorage.removeItem(LS_RETURN_TO);

  return { returnTo };
}

// --- Refresh ---

export async function refreshAccessToken(): Promise<boolean> {
  const refresh = localStorage.getItem(LS_REFRESH);
  if (!refresh) return false;
  const clientId = localStorage.getItem(LS_CLIENT_ID);
  if (!clientId) return false;

  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refresh,
    client_id: clientId,
    resource: RESOURCE,
  });

  try {
    const tok = await mcpProxy<TokenResponse>("token", Object.fromEntries(body));
    persistTokens(tok);
    return true;
  } catch {
    return false;
  }
}

// --- Logout ---

export async function mcpSignOut(): Promise<void> {
  const refresh = localStorage.getItem(LS_REFRESH);
  const clientId = localStorage.getItem(LS_CLIENT_ID);
  // Best-effort revoke (don't block UX on failure)
  if (refresh && clientId) {
    try {
      await mcpProxy("revoke", { token: refresh, client_id: clientId });
    } catch {
      // ignore
    }
  }
  clearMcpAccessToken();
  localStorage.removeItem(LS_REFRESH);
  localStorage.removeItem("smplify_tenant_id");
}
